import { Flashbooks } from '@prisma/client';
import qs from 'qs';
import API_URL from '../apiconfig';
import client from '../utils/react-query/client';
import axios from './api';
import {
  BusinessFlashDay,
  CreateFlashDesignPayload,
  FlashDesignsListMeta,
  FlashDesignWithRelations,
  FlashbookWithRelations,
  UpdateDesignsSortOrderPayload,
  UpdateFlashDesignPayload,
  UpdateFlashbookPayload,
} from '../types/flashbooks';

export const getFlashbook = async (employeeId: string) => {
  const response = await axios.get(`${API_URL}/v1/flashbooks`, {
    params: {
      employeeId,
    },
  });

  return response.data.result as FlashbookWithRelations;
};

export const getBusinessFlashbook = async (businessId: string) => {
  const response = await axios.get(`${API_URL}/v1/flashbooks`, {
    params: {
      businessId,
    },
  });

  return response.data.result as Flashbooks;
};

export const getBusinessFlashDays = async (businessId: string) => {
  const response = await axios.get(`${API_URL}/v1/flashbooks/flash-days`, {
    params: {
      businessId,
    },
  });

  return response.data.result as BusinessFlashDay[];
};

export const updateFlashbook = async ({
  flashbookId,
  payload,
}: {
  flashbookId: string;
  payload: UpdateFlashbookPayload;
}) => {
  const response = await axios.patch(
    `${API_URL}/v1/flashbooks/${flashbookId}`,
    payload,
  );

  return response.data.result as Flashbooks;
};

export const getFlashbookDesigns = async ({
  flashbookId,
  isArchived,
  cursor,
  limit,
}: {
  flashbookId: string;
  isArchived?: boolean;
  cursor?: number;
  limit?: number;
}) => {
  const response = await axios.get(
    `${API_URL}/v1/flashbooks/${flashbookId}/designs`,
    {
      params: {
        isArchived,
        cursor,
        limit,
      },
    },
  );

  return response.data as {
    result: FlashDesignWithRelations[];
    meta: FlashDesignsListMeta;
  };
};

export const invalidateFlashbookDesigns = async () => {
  await client.invalidateQueries({ queryKey: ['flashbook-designs'] });
};

export const getFlashbookDesign = async ({
  flashbookId,
  flashDesignId,
}: {
  flashbookId: string;
  flashDesignId: number | string;
}) => {
  const response = await axios.get(
    `${API_URL}/v1/flashbooks/${flashbookId}/designs/${flashDesignId}`,
  );

  return response.data.result as FlashDesignWithRelations;
};

export const createFlashbookDesign = async ({
  flashbookId,
  payload,
}: {
  flashbookId: string;
  payload: CreateFlashDesignPayload;
}) => {
  const response = await axios.post(
    `${API_URL}/v1/flashbooks/${flashbookId}/designs`,
    payload,
  );

  return response.data.result as FlashDesignWithRelations;
};

export const updateFlashbookDesign = async ({
  flashbookId,
  flashDesignId,
  payload,
}: {
  flashbookId: string;
  flashDesignId: number;
  payload: UpdateFlashDesignPayload;
}) => {
  const response = await axios.patch(
    `${API_URL}/v1/flashbooks/${flashbookId}/designs/${flashDesignId}`,
    payload,
  );

  return response.data.result as FlashDesignWithRelations;
};

export const updateDesignsSortOrder = async ({
  flashbookId,
  payload,
}: {
  flashbookId: string;
  payload: UpdateDesignsSortOrderPayload;
}) => {
  const response = await axios.patch(
    `${API_URL}/v1/flashbooks/${flashbookId}/designs/sort-orders`,
    payload,
  );

  return response.data.result as { success: boolean };
};

export const getTags = async ({
  flashbookId,
  query,
  excludeTags,
}: {
  flashbookId: string;
  query?: string;
  excludeTags?: string[];
}) => {
  const response = await axios.get(
    `${API_URL}/v1/flashbooks/${flashbookId}/tags`,
    {
      params: {
        query,
        excludeTags,
      },
      paramsSerializer: params =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    },
  );

  return response.data.result as string[];
};

export const getBusinessTags = async ({
  businessId,
  query,
  excludeTags,
}: {
  businessId: string;
  query?: string;
  excludeTags?: string[];
}) => {
  const response = await axios.get(`${API_URL}/v1/flashbooks/business-tags`, {
    params: {
      businessId,
      query,
      excludeTags,
    },
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

  return response.data.result as string[];
};
